import { useEffect, useMemo, useState } from "react"
import { useSearchParams } from "react-router-dom"

import { ProductImage } from "scrivito/Objs/Product/Components/ProductImage"
import { NOT_FOUND } from "shared/constants/permalinks"
import { useEvent } from "shared/lib/useEvent"
import { useTitle } from "shared/lib/usePageTitle"
import { ProductConfiguratorMode } from "shared/types/ProductConfiguratorMode"
import { ProductConfiguratorModeSelectProps } from "shared/ui/product-configurator-mode-select/ProductConfiguratorModeSelect"

import { useProductConfigurationLazyQuery } from "entities/dim-configuration/graphql/product-configuration.queries.generated"
import { getProductConfigurationQueryVariables } from "entities/dim-configuration/lib/getProductConfigurationQueryVariables"
import { getProductType } from "entities/products/lib/getProductType"
import { useExternalDimensioningModelAvailability } from "entities/products/model/useExternalDimensioningModelAvailability"
import { useProductConfigurationModelAvailability } from "entities/products/model/useProductConfigurationModelAvailability"
import { useProductObjLoad } from "entities/products/model/useProductObjLoad"
import { useNavigateToPermalink } from "features/navigation/model/useNavigateToPermalink"
import {
  ProductConfiguratorResult,
  useProductConfiguratorModel
} from "widgets/product-configurator/model/useProductConfiguratorModel"

import { ProductConfigurationPageProps } from "../ProductConfigurationPage"
import { ProductConfigurationPageViewModel } from "../ui/ProductConfigurationPageView"

export function useProductConfigurationPageModel({
  variantKey
}: ProductConfigurationPageProps): ProductConfigurationPageViewModel {
  const [productConfiguratorMode, setProductConfiguratorMode] =
    useState<ProductConfiguratorMode>()
  const [searchParams] = useSearchParams()

  const configurationKey = useMemo(
    () => searchParams.get("configurationKey"),
    [searchParams]
  )

  const { title, productType, productObj, isLoadingObj } =
    useProductObjLoad(variantKey)
  useTitle(title)

  const [fetchConfiguration] = useProductConfigurationLazyQuery()

  const handleSubmit = useEvent(async (result: ProductConfiguratorResult) => {
    const configuration = await fetchConfiguration({
      variables: getProductConfigurationQueryVariables({
        productType: getProductType(result.variantKey),
        configurationKey: result.configurationKey,
        dimensioningKey: result.externalDimensioningResult?.transactionId,
        variantKey: result.variantKey
      })
    })
    window.parent.postMessage(
      {
        type: "product_configuration",
        event: "configuration_finished",
        variantKey: result.variantKey,
        addonKeys: configuration.data?.productConfiguration?.accessories || []
      },
      "*"
    )
  })

  const productConfiguratorModel = useProductConfiguratorModel({
    variantKey,
    configurationKey,
    mode: productConfiguratorMode,
    onSubmit: handleSubmit
  })

  const { isConfigurationAvailable } =
    useProductConfigurationModelAvailability(productType)

  const { isExternalDimensioningAvailable } =
    useExternalDimensioningModelAvailability(productType)

  const { navigate } = useNavigateToPermalink()

  const productConfiguratorModeSelectModel =
    useMemo<ProductConfiguratorModeSelectProps>(
      () => ({
        image: <ProductImage obj={productObj} />,
        isConfigurationAvailable,
        isExternalDimensioningAvailable,
        onConfigurationSelect: () =>
          setProductConfiguratorMode(ProductConfiguratorMode.CONFIGURATION),
        onDimensioningSelect: () =>
          setProductConfiguratorMode(ProductConfiguratorMode.DIMENSIONING)
      }),
      [
        productObj,
        isConfigurationAvailable,
        isExternalDimensioningAvailable,
        setProductConfiguratorMode
      ]
    )

  useEffect(() => {
    if (
      !productObj &&
      !isLoadingObj &&
      !productConfiguratorModel?.schema &&
      !productConfiguratorModel?.isLoading
    ) {
      navigate(NOT_FOUND)
    }
  }, [
    productObj,
    productConfiguratorModel?.schema,
    productConfiguratorModel?.isLoading,
    isLoadingObj
  ])

  return {
    productConfiguratorModel,
    productConfiguratorMode,
    productConfiguratorModeSelectModel
  }
}
